import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useInvalidateBanking } from '@/components/banking/line-editor';
import { AmountInput } from '@/components/amount-input';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, api } from '@/lib/api';
import {
  useBankAccounts,
  useReconciliations,
  type Reconciliation,
  type ReconcileWorkspace,
} from '@/lib/banking';
import { useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/banking/reconcile/')({
  component: ReconcilePage,
  validateSearch: (s: Record<string, unknown>): { accountId?: string } =>
    typeof s['accountId'] === 'string' ? { accountId: s['accountId'] } : {},
});

const STATUS: Record<
  Reconciliation['status'],
  { label: string; variant: 'secondary' | 'outline' | 'default' }
> = {
  in_progress: { label: 'In progress', variant: 'outline' },
  finished: { label: 'Reconciled', variant: 'secondary' },
  undone: { label: 'Undone', variant: 'outline' },
};

/** Screen B4 (document 06): Reconcile, step 1 and history. */
function ReconcilePage() {
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const banks = useBankAccounts();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const invalidate = useInvalidateBanking();
  const accountId = search.accountId ?? banks.data?.[0]?.accountId ?? null;
  const bank = banks.data?.find((b) => b.accountId === accountId) ?? null;
  const history = useReconciliations(accountId);
  const [endDate, setEndDate] = useState('');
  const [endBalance, setEndBalance] = useState('');
  const [negative, setNegative] = useState(false);
  const [tried, setTried] = useState(false);
  const [undoing, setUndoing] = useState<Reconciliation | null>(null);
  const [deleting, setDeleting] = useState<Reconciliation | null>(null);
  useEffect(() => {
    setEndDate('');
    setEndBalance('');
    setNegative(false);
    setTried(false);
  }, [accountId]);

  const start = useMutation({
    mutationFn: () =>
      api<ReconcileWorkspace>(`/orgs/${orgId}/banking/reconciliations`, {
        method: 'POST',
        body: JSON.stringify({
          accountId,
          statementDate: endDate,
          statementBalance: `${negative ? '-' : ''}${endBalance || '0'}`,
        }),
      }),
    onSuccess: async (w) => {
      await invalidate();
      void navigate({
        to: '/banking/reconcile/$reconciliationId',
        params: { reconciliationId: w.reconciliation.id },
      });
    },
  });
  const remove = useMutation({
    mutationFn: (r: Reconciliation) =>
      api(`/orgs/${orgId}/banking/reconciliations/${r.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await invalidate();
      setDeleting(null);
      toast('Reconciliation deleted. Nothing was changed in the books.');
    },
  });

  const open = history.data?.find((r) => r.status === 'in_progress') ?? null;
  const card = bank?.accountType === 'credit_card';

  return (
    <>
      <PageHeader
        title="Reconcile"
        description="Compare an account with its bank statement, tick what cleared, and finish when the difference is zero."
      />
      {!orgId ? (
        <EmptyState title="Select a client" />
      ) : banks.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : banks.isError ? (
        <EmptyState title="Could not load bank accounts" hint={banks.error.message} />
      ) : !bank ? (
        <EmptyState title="No bank accounts to reconcile" />
      ) : (
        <>
          <div className="mb-6 max-w-3xl rounded-lg border bg-card p-5">
            <h2 className="mb-3 text-base font-semibold">
              Which account do you want to reconcile?
            </h2>
            <ErrorBanner>{start.error?.message ?? null}</ErrorBanner>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Account">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={bank.accountId}
                    onChange={(e) => void navigate({ search: { accountId: e.target.value } })}
                  >
                    {banks.data.map((b) => (
                      <option key={b.accountId} value={b.accountId}>
                        {b.code} · {b.name}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <div className="flex flex-col justify-end text-sm">
                <div className="text-muted-foreground">
                  {bank.lastReconciledDate
                    ? `Last statement ending date ${displayDate(bank.lastReconciledDate)}`
                    : 'Never reconciled'}
                </div>
              </div>
            </div>
            {open ? (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
                <span>
                  A reconciliation to {displayDate(open.statementDate)} (ending balance{' '}
                  {open.currency} {formatAmount(open.statementBalance)}) was saved for later.
                </span>
                <Button size="sm" asChild className="ml-auto">
                  <Link
                    to="/banking/reconcile/$reconciliationId"
                    params={{ reconciliationId: open.id }}
                  >
                    Resume reconciling
                  </Link>
                </Button>
              </div>
            ) : canEdit ? (
              <>
                <h3 className="mb-2 mt-5 text-sm font-semibold">Add the following information</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Field label="Beginning balance" hint="From the last reconciliation.">
                    {(id) => (
                      <input
                        id={id}
                        readOnly
                        className="tnum h-9 rounded-md border border-input bg-muted px-3 text-right text-sm"
                        value={formatAmount(bank.lastReconciledBalance ?? '0')}
                      />
                    )}
                  </Field>
                  <Field
                    label="Ending balance *"
                    hint={card ? 'The amount owed on the statement.' : undefined}
                    error={tried && !endBalance ? 'Enter the statement ending balance' : undefined}
                  >
                    {(id) => (
                      <div className="flex items-center gap-2">
                        <AmountInput
                          id={id}
                          className="h-9 w-full rounded-md border border-input bg-card px-3 text-right text-sm"
                          value={endBalance}
                          invalid={tried && !endBalance}
                          onValueChange={setEndBalance}
                        />
                      </div>
                    )}
                  </Field>
                  <Field
                    label="Ending date *"
                    error={tried && !endDate ? 'Enter the statement ending date' : undefined}
                  >
                    {(id) => (
                      <DateInput
                        id={id}
                        value={endDate}
                        onChange={setEndDate}
                        invalid={tried && !endDate}
                      />
                    )}
                  </Field>
                </div>
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    className="size-3.5 accent-primary"
                    checked={negative}
                    onChange={(e) => setNegative(e.target.checked)}
                  />
                  The ending balance is negative ({card ? 'the card is in credit' : 'overdrawn'})
                </label>
                <div className="mt-4">
                  <Button
                    disabled={start.isPending}
                    onClick={() => {
                      setTried(true);
                      if (endDate && endBalance !== '') start.mutate();
                    }}
                  >
                    {start.isPending ? 'Starting…' : 'Start reconciling'}
                  </Button>
                </div>
              </>
            ) : null}
          </div>

          <h2 className="mb-2 text-base font-semibold">History for {bank.name}</h2>
          {history.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !history.data?.length ? (
            <EmptyState title="No reconciliations yet" />
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">Statement ending date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ending balance</TableHead>
                    <TableHead className="text-right">Beginning balance</TableHead>
                    <TableHead>Reconciled on</TableHead>
                    <TableHead className="w-[220px] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.data.map((r) => (
                    <TableRow key={r.id} data-testid="reconciliation-row">
                      <TableCell className="tnum">{displayDate(r.statementDate)}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS[r.status].variant}>{STATUS[r.status].label}</Badge>
                        {r.status === 'undone' && r.undoReason ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {r.undoneByName ? `${r.undoneByName}: ` : ''}
                            {r.undoReason}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="tnum text-right">
                        {formatAmount(r.statementBalance)}
                      </TableCell>
                      <TableCell className="tnum text-right">
                        {formatAmount(r.beginningBalance)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.finishedAt
                          ? `${displayDate(r.finishedAt.slice(0, 10))}${r.finishedByName ? ` · ${r.finishedByName}` : ''}`
                          : ''}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.status === 'in_progress' ? (
                          <>
                            <Button size="sm" variant="link" className="px-2" asChild>
                              <Link
                                to="/banking/reconcile/$reconciliationId"
                                params={{ reconciliationId: r.id }}
                              >
                                Resume
                              </Link>
                            </Button>
                            {canEdit ? (
                              <Button
                                size="sm"
                                variant="link"
                                className="px-2 text-destructive"
                                onClick={() => setDeleting(r)}
                              >
                                Delete
                              </Button>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="link" className="px-2" asChild>
                              <Link
                                to="/banking/reconcile/$reconciliationId/report"
                                params={{ reconciliationId: r.id }}
                              >
                                View report
                              </Link>
                            </Button>
                            {canEdit && r.status === 'finished' && r.isLatest ? (
                              <Button
                                size="sm"
                                variant="link"
                                className="px-2 text-destructive"
                                onClick={() => setUndoing(r)}
                              >
                                Undo
                              </Button>
                            ) : null}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
      <UndoDialog rec={undoing} onClose={() => setUndoing(null)} />
      <Dialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete the reconciliation in progress?</DialogTitle>
            <DialogDescription>
              The ticks saved for later are discarded. Nothing in the books changes.
            </DialogDescription>
          </DialogHeader>
          {remove.error ? <p className="text-sm text-destructive">{remove.error.message}</p> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Undo the last reconciliation (accountants; ConfirmDialog with a typed reason, audited). */
function UndoDialog({ rec, onClose }: { rec: Reconciliation | null; onClose: () => void }) {
  const orgId = useOrgId();
  const invalidate = useInvalidateBanking();
  const [reason, setReason] = useState('');
  useEffect(() => setReason(''), [rec]);
  const undo = useMutation({
    mutationFn: () =>
      api<Reconciliation>(`/orgs/${orgId}/banking/reconciliations/${rec!.id}/undo`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      }),
    onSuccess: async () => {
      await invalidate();
      toast('Reconciliation undone. Its transactions are no longer reconciled.');
      onClose();
    },
  });
  return (
    <Dialog
      open={Boolean(rec)}
      onOpenChange={(o) => {
        if (!o) {
          undo.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Undo the reconciliation to {rec ? displayDate(rec.statementDate) : ''}?
          </DialogTitle>
          <DialogDescription>
            Every transaction it cleared goes back to uncleared, and can be changed or reversed
            again. The reconciliation stays in the history, marked undone. Nothing is posted.
          </DialogDescription>
        </DialogHeader>
        <Field label="Reason" hint="Recorded in the audit log.">
          {(id) => <Textarea id={id} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        {undo.error ? (
          <p role="alert" className="text-sm text-destructive">
            {undo.error instanceof ApiError ? undo.error.message : String(undo.error)}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length < 3 || undo.isPending}
            onClick={() => undo.mutate()}
          >
            {undo.isPending ? 'Undoing…' : 'Undo reconciliation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
